import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminCoachListResult,
  type AdminCoachRow,
} from "api/adminApi";
import {
  Badge,
  Card,
  ErrorBanner,
  HelpTip,
  Input,
  NUMERIC,
  SectionHeader,
  Select,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
} from "pages/admin/ui";
import { todayInputValue } from "pages/admin/user/userFormat";
import CoachDetailModal from "./CoachDetailModal";
import {
  capLoad,
  leaveStateOf,
  monthDayLabel,
  rosterSummary,
} from "./coachFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";
const COLUMN_COUNT = 6;

type StatusFilter = "active" | "inactive";
type MasterFilter = "" | "on" | "off";

// 캡 대비 색: 90% 부터 주의, 100% 부터 자동 배정 후보에서 빠진다
const loadTone = (percent: number | null) => {
  if (percent == null) return { bar: "bg-slate-300", text: "text-slate-500" };
  if (percent >= 100) return { bar: "bg-rose-500", text: "text-rose-600" };
  if (percent >= 90) return { bar: "bg-amber-500", text: "text-amber-700" };
  return { bar: "bg-[#0F55A7]", text: "text-slate-900" };
};

/**
 * 코치 관리: 실무. 내담자를 배정받는 코치(관리자 코치 제외)의 캡·배정 조건·휴가. 목록은 회원번호(userId)순.
 * 목록은 수십 행이라 한 번에 받아 화면에서 거른다. 행을 누르면 상세 모달에서 고치고, 저장하면 목록을 다시 받는다.
 */
export default function CoachSection() {
  const [data, setData] = useState<AdminCoachListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [master, setMaster] = useState<MasterFilter>("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);
  const today = todayInputValue();

  // 저장 직후 다시 받는 요청과 겹쳐도 마지막 응답만 반영한다
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.coachList();
      if (seq !== requestSeq.current) return;
      if (res.code === 0 && res.result) setData(res.result);
      else setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      const code = (e as AxiosError).response?.status;
      setError(
        code === 403 ? "권한이 없는 계정이에요" : FALLBACK_ERROR_MESSAGE
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const coaches = data?.coaches ?? [];
  const keyword = query.trim();
  const rows = coaches.filter(
    (r) =>
      (status === "active") === r.isActive &&
      (master === "" || (master === "on") === !r.isMasterPlanExcluded) &&
      (keyword === "" || r.name.includes(keyword))
  );
  const summary = rosterSummary(
    coaches,
    data?.recentDays ?? 0,
    data?.recentStartedCount ?? 0,
    today
  );
  const openRow = coaches.find((r) => r.coachId === openId) ?? null;

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title="실무 코치" count={`${rows.length}명`} />

      <p className="text-sm text-slate-500">
        내담자를 배정받는 코치의 캡·배정 조건·휴가를 관리해요.
      </p>

      {/* 활성 코치 기준 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="px-4 py-3">
          <p className="flex items-center text-xs font-medium text-slate-700">
            전체 캡 대비 담당 인원
            <HelpTip text="활성 코치의 담당 인원 합계 ÷ 전체 캡 합계. 남은 자리 = 전체 캡 − 담당 인원" />
          </p>
          <p className={`mt-1 text-xl font-semibold text-slate-900 ${NUMERIC}`}>
            {data == null || summary.percent == null
              ? "–"
              : `${summary.percent}%`}{" "}
            {data && (
              <span className="text-sm font-normal text-slate-600">
                {summary.assignedSum.toLocaleString()} /{" "}
                {summary.capSum.toLocaleString()}
              </span>
            )}
          </p>
          <span className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <span
              className={`block h-full rounded-full ${loadTone(summary.percent).bar}`}
              style={{ width: `${Math.min(summary.percent ?? 0, 100)}%` }}
            />
          </span>
          <p
            className={`mt-1.5 text-sm font-medium ${NUMERIC} ${
              summary.left > 0 ? "text-slate-700" : "text-rose-600"
            }`}
          >
            {data == null
              ? ""
              : summary.left > 0
                ? `남은 자리 ${summary.left.toLocaleString()}명`
                : summary.left === 0
                  ? "남은 자리 없음"
                  : `${(-summary.left).toLocaleString()}명 초과`}
          </p>
        </Card>
        <Card className="px-4 py-3">
          <p className="flex items-center text-xs font-medium text-slate-700">
            전체 캡 마감 예상
            <HelpTip
              text={`남은 자리 ÷ 최근 ${data?.recentDays ?? 3}일(오늘 제외) 하루 평균 신규 배정. 종료·만료로 빠지는 인원은 반영하지 않아 실제보다 이르게 나와요.`}
            />
          </p>
          <p
            className={`mt-1 text-xl font-semibold ${NUMERIC} ${
              data != null && summary.left <= 0
                ? "text-rose-600"
                : summary.daysToFull != null && summary.daysToFull <= 7
                  ? "text-amber-700"
                  : "text-slate-900"
            }`}
          >
            {data == null ? (
              "–"
            ) : summary.left <= 0 ? (
              "이미 다 참"
            ) : summary.daysToFull == null ? (
              "–"
            ) : (
              <>
                {summary.daysToFull}일 후{" "}
                <span className="text-sm font-medium text-slate-700">
                  {summary.fullDate && monthDayLabel(summary.fullDate)}
                </span>
              </>
            )}
          </p>
          <p className={`mt-0.5 text-xs text-slate-600 ${NUMERIC}`}>
            {data == null
              ? ""
              : summary.perDay > 0
                ? `최근 ${data.recentDays}일 하루 평균 ${
                    Math.round(summary.perDay * 10) / 10
                  }명 배정 기준`
                : `최근 ${data.recentDays}일 신규 배정 없음`}
          </p>
        </Card>
        <Card className="px-4 py-3">
          <p className="flex items-center text-xs font-medium text-slate-700">
            캡 도달 코치
            <HelpTip text="담당 인원이 전체 캡 이상이라 자동 배정 후보에서 빠진 코치" />
          </p>
          <p className={`mt-1 text-xl font-semibold text-slate-900 ${NUMERIC}`}>
            {data == null ? "–" : `${summary.capReachedCount}명`}
          </p>
          <p className={`mt-0.5 text-xs text-slate-600 ${NUMERIC}`}>
            {data == null ? "" : `활성 ${summary.activeCount}명 중`}
          </p>
        </Card>
        <Card className="px-4 py-3">
          <p className="flex items-center text-xs font-medium text-slate-700">
            휴가 중 코치
            <HelpTip text="오늘이 휴가 기간에 걸린 활성 코치" />
          </p>
          <p className={`mt-1 text-xl font-semibold text-slate-900 ${NUMERIC}`}>
            {data == null ? "–" : `${summary.onLeaveCount}명`}
          </p>
          <p className={`mt-0.5 text-xs text-slate-600 ${NUMERIC}`}>
            {data == null ? "" : `휴가 예정 ${summary.leavePlannedCount}명`}
          </p>
        </Card>
      </div>

      {/* 필터: 상태 기본은 활성 */}
      <div className="flex flex-wrap items-end gap-2">
        <Select
          fullWidth={false}
          className="w-32"
          aria-label="상태"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        >
          <option value="active">활성</option>
          <option value="inactive">비활성</option>
        </Select>
        <Select
          fullWidth={false}
          className="w-44"
          aria-label="마스터 플랜"
          value={master}
          onChange={(e) => setMaster(e.target.value as MasterFilter)}
        >
          <option value="">마스터 플랜 전체</option>
          <option value="on">마스터 플랜 담당</option>
          <option value="off">마스터 플랜 미담당</option>
        </Select>
        <Input
          fullWidth={false}
          className="w-44"
          aria-label="이름 검색"
          placeholder="이름 검색"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Table>
        <THead>
          <TH>코치</TH>
          <TH
            numeric
            help="지금 맡고 있는 진행 중 내담자 수 (인사이더 제외). 자동 배정기와 같은 기준이에요."
          >
            담당 인원
          </TH>
          <TH
            numeric
            help="동시에 맡을 수 있는 최대 인원. 담당 인원이 여기 닿으면 자동 배정 후보에서 빠져요. 0이면 자동 배정을 받지 않아요."
          >
            전체 캡
          </TH>
          <TH help="담당 인원 ÷ 전체 캡과 남은 자리(전체 캡 − 담당 인원). 100%가 되면 자동 배정 후보에서 빠져요. 모든 코치가 다 차면 캡을 넘겨 배정돼 100%를 넘을 수 있어요.">
            캡 대비
          </TH>
          <TH help="마스터 플랜 내담자를 배정받는지 여부">마스터 플랜</TH>
          <TH help="등록된 휴가 중 가장 가까운 것. 공지일 11시부터 신규 배정이 멈추고, 휴가 끝난 다음날 0시에 다시 받아요.">
            휴가
          </TH>
        </THead>
        <tbody>
          {loading && !data ? (
            <TSkeleton colSpan={COLUMN_COUNT} />
          ) : rows.length === 0 ? (
            <TEmpty colSpan={COLUMN_COUNT}>조건에 맞는 코치가 없어요</TEmpty>
          ) : (
            rows.map((row) => (
              <CoachTableRow
                key={row.coachId}
                row={row}
                today={today}
                onOpen={() => setOpenId(row.coachId)}
              />
            ))
          )}
        </tbody>
      </Table>

      {openRow && (
        <CoachDetailModal
          row={openRow}
          today={today}
          onChanged={load}
          onClose={() => setOpenId(null)}
        />
      )}
    </section>
  );
}

function CoachTableRow({
  row,
  today,
  onOpen,
}: {
  row: AdminCoachRow;
  today: string;
  onOpen: () => void;
}) {
  const { percent, left } = capLoad(
    row.assignedPatientCount,
    row.assignCapCount
  );
  const tone = loadTone(percent);
  const leave = row.leaves[0];

  return (
    <TRow className="cursor-pointer">
      <TD onClick={onOpen}>
        <span
          className={`font-medium ${row.isActive ? "text-slate-900" : "text-slate-500"}`}
        >
          {row.name}
        </span>
      </TD>
      <TD numeric onClick={onOpen} className={`font-medium ${tone.text}`}>
        {row.assignedPatientCount}
      </TD>
      <TD numeric onClick={onOpen}>
        {row.assignCapCount === 0 ? (
          <span className="text-slate-400">0</span>
        ) : (
          row.assignCapCount
        )}
      </TD>
      <TD onClick={onOpen}>
        <span className="flex items-center gap-2">
          <span className="block h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
            <span
              className={`block h-full rounded-full ${tone.bar}`}
              style={{ width: `${Math.min(percent ?? 0, 100)}%` }}
            />
          </span>
          <span className={`w-10 text-xs ${NUMERIC} ${tone.text}`}>
            {percent == null ? "–" : `${percent}%`}
          </span>
          {percent != null && (
            <span
              className={`text-xs ${NUMERIC} ${
                left < 0
                  ? "font-medium text-rose-600"
                  : left === 0
                    ? "text-amber-700"
                    : "text-slate-700"
              }`}
            >
              {left < 0
                ? `${-left}명 초과`
                : left === 0
                  ? "남은 자리 없음"
                  : `${left}자리 남음`}
            </span>
          )}
        </span>
      </TD>
      <TD onClick={onOpen}>
        {row.isMasterPlanExcluded ? (
          <Badge tone="idle">미담당</Badge>
        ) : (
          <Badge tone="ok">담당</Badge>
        )}
      </TD>
      <TD onClick={onOpen}>
        {!row.isActive || !leave ? (
          <span className="text-slate-300">-</span>
        ) : (
          <span className="flex items-center gap-1.5">
            {leaveStateOf(leave, today) === "on" ? (
              <Badge tone="warn">휴가 중</Badge>
            ) : (
              <Badge tone={row.assignDailyCapCount === 0 ? "info" : "idle"}>
                휴가 예정
              </Badge>
            )}
            <span className={`text-xs text-slate-500 ${NUMERIC}`}>
              {monthDayLabel(leave.leaveFromDate)} ~{" "}
              {monthDayLabel(leave.leaveToDate)}
            </span>
            {/* 공지일 11시에 일일 캡이 0 이 된 뒤: 휴가 전인데 신규 배정이 멈춰 있다 */}
            {leaveStateOf(leave, today) === "planned" &&
              row.assignDailyCapCount === 0 && (
                <span className="text-xs text-amber-700">신규 배정 중단</span>
              )}
          </span>
        )}
      </TD>
    </TRow>
  );
}
